import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '@core/models/user';
import { Company } from '@core/models/company';
import { UserService } from '@core/services/user.service';
import { AuthService } from '@core/services/auth.service';
import { CompanyService } from '@core/services/company.service';
import { ThemeService } from '@core/services/theme.service';
import { ProfileValidationService } from '@core/services/profile-validation.service';
import { RegistrationErrorService } from '@core/services/registration-error.service';
import { FormValidationService } from '@core/services/form-validation.service';
import { CompanyInviteService } from '@core/services/company-invite.service';
import { LoggingService } from '@core/services/logging.service';
import { take } from 'rxjs/operators';

/**
 * ProfileCompletionComponent
 *
 * Collects missing profile information after OAuth authentication or email signup.
 * Ensures all users have firstName, lastName, and optionally phone number.
 * This component is used to bridge the gap where OAuth providers may not provide
 * complete profile information or when users need to complete their profile.
 */
@Component({
  selector: 'app-profile-completion',
  templateUrl: './profile-completion.component.html',
  styleUrl: './profile-completion.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileCompletionComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private companyService = inject(CompanyService);
  private themeService = inject(ThemeService);
  private profileValidationService = inject(ProfileValidationService);
  private errorService = inject(RegistrationErrorService);
  private formValidationService = inject(FormValidationService);
  private companyInviteService = inject(CompanyInviteService);
  private loggingService = inject(LoggingService);

  // Constants
  private static readonly PROFILE_COMPLETION_THRESHOLD = 80;

  profileForm!: FormGroup;
  user?: User;
  loading = false;
  serverMessage?: string;

  // Track which fields need completion
  needsFirstName = false;
  needsLastName = false;
  needsPhone = false;
  needsJobTitle = false;
  needsContactMethod = false;

  // Tier information
  userTier: string = 'free';
  isTrialOrPremium = false;

  constructor() {
    this.themeService.setSelectPlanPageLayout(true);
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadCurrentUser();
  }

  /**
   * Initialize the profile completion form with validators based on user tier
   */
  private initializeForm(): void {
    // Start with basic validators - will be updated in analyzeProfileCompleteness
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      phone: ['', [this.formValidationService.phoneValidator()]], // Conditional validation based on tier
      jobTitle: ['', [this.formValidationService.jobTitleValidator()]], // Conditional validation based on tier
      preferredContactMethod: ['email'], // Default to email
    });
  }

  /**
   * Load current user and determine which fields need completion
   */
  private async loadCurrentUser(): Promise<void> {
    try {
      this.userService
        .getUserModel()
        .pipe(take(1))
        .subscribe((user) => {
          if (user) {
            this.user = user;
            this.analyzeProfileCompleteness();
            this.populateExistingData();

            // Auto-advance when profile is already complete for the current tier
            const shouldAutoProceed =
              !this.needsFirstName &&
              !this.needsLastName &&
              !this.hasTierSpecificIncompleteFields();

            if (shouldAutoProceed) {
              // slight delay to allow UI to render success state then navigate
              setTimeout(() => this.proceedToNextStep(), 100);
            }
          } else {
            // No user found - redirect to login
            this.router.navigate(['/login']);
          }
        });
    } catch (error) {
      console.error('Error loading user:', error);
      this.serverMessage = 'Error loading user profile. Please try again.';
    }
  }

  /**
   * Analyze which profile fields are missing or incomplete based on user tier
   */
  private analyzeProfileCompleteness(): void {
    if (!this.user) return;

    // Determine user tier and requirements
    this.userTier = this.user.intendedTier || 'free';
    this.isTrialOrPremium =
      this.userTier === 'trial' || this.userTier === 'premium';

    // Basic fields required for all tiers
    this.needsFirstName =
      !this.user.firstName || this.user.firstName.trim().length === 0;
    this.needsLastName =
      !this.user.lastName || this.user.lastName.trim().length === 0;

    // Tier-specific field requirements
    if (this.isTrialOrPremium) {
      this.needsPhone = !this.user.phone || this.user.phone.trim().length === 0;
      this.needsJobTitle =
        !this.user.jobTitle || this.user.jobTitle.trim().length === 0;
      this.needsContactMethod = !this.user.preferredContactMethod;

      // Add tier-specific validators
      this.profileForm.get('phone')?.setValidators([Validators.required]);
      this.profileForm
        .get('jobTitle')
        ?.setValidators([Validators.required, Validators.minLength(2)]);
      this.profileForm
        .get('preferredContactMethod')
        ?.setValidators([Validators.required]);
    } else {
      // Free tier - phone and jobTitle are optional
      this.needsPhone = false; // Don't require, but allow entry
      this.needsJobTitle = false;
      this.needsContactMethod = false;

      // Remove validators for optional fields
      this.profileForm.get('phone')?.clearValidators();
      this.profileForm.get('jobTitle')?.clearValidators();
      this.profileForm.get('preferredContactMethod')?.clearValidators();
    }

    // Update form validation status
    this.profileForm.get('phone')?.updateValueAndValidity();
    this.profileForm.get('jobTitle')?.updateValueAndValidity();
    this.profileForm.get('preferredContactMethod')?.updateValueAndValidity();
  }

  /**
   * Populate form with existing user data
   */
  private populateExistingData(): void {
    if (!this.user) return;

    this.profileForm.patchValue({
      firstName: this.user.firstName || '',
      lastName: this.user.lastName || '',
      phone: this.user.phone || '',
      jobTitle: this.user.jobTitle || '',
      preferredContactMethod: this.user.preferredContactMethod || 'email',
    });

    // Make completed fields read-only
    if (!this.needsFirstName) {
      this.profileForm.get('firstName')?.disable();
    }
    if (!this.needsLastName) {
      this.profileForm.get('lastName')?.disable();
    }
  }

  /**
   * Handle form submission to save profile data
   */
  async onSubmit(): Promise<void> {
    if (!this.profileForm.valid || !this.user) {
      // Use improved form validation with better error messages
      const fieldLabels = {
        firstName: 'First Name',
        lastName: 'Last Name',
        phone: 'Phone Number',
        jobTitle: 'Job Title',
      };

      if (!this.user) {
        console.error('🚫 PROFILE COMPLETION - No user session');
        this.errorService.addError(
          'User session expired. Please log in again.',
          'error',
        );
        return;
      }

      if (
        !this.formValidationService.validateFormFields(
          this.profileForm,
          fieldLabels,
        )
      ) {
        this.formValidationService.markFormGroupTouched(this.profileForm);
        return;
      }
    }

    this.errorService.setLoading('profile-completion', true);
    this.loading = true;
    this.errorService.clearErrors();

    try {
      const formData = this.profileForm.getRawValue(); // Use getRawValue to include disabled fields

      // Update user model with new profile data (user is guaranteed to exist due to check above)
      this.user!.firstName = formData.firstName;
      this.user!.lastName = formData.lastName;
      this.user!.phone = formData.phone || null;
      this.user!.jobTitle = formData.jobTitle || null;
      this.user!.preferredContactMethod =
        formData.preferredContactMethod || 'email';

      // Update profile completion tracking
      const completionPercentage =
        this.profileValidationService.calculateProfileCompletionLevel(
          this.user!,
        );
      this.user!.profileCompletionLevel =
        completionPercentage >=
          ProfileCompletionComponent.PROFILE_COMPLETION_THRESHOLD
          ? 'complete'
          : 'basic';

      // Save to Firebase
      await this.userService.update(this.user!, this.user!.uid);

      // Update local user model
      this.userService.setUserModel(this.user!);

      // Show success message
      this.errorService.showSuccess('Profile updated successfully!');

      // If there is a pending invite code (from an invite link), redeem it now
      // that the user profile document exists in Firestore.
      const pendingInviteCode =
        this.route.snapshot.queryParams['inviteCode'] as string | undefined;
      if (pendingInviteCode) {
        await this.redeemPendingInvite(pendingInviteCode);
      } else {
        // Normal registration flow: go to company creation or dashboard
        this.proceedToNextStep();
      }
    } catch (error) {
      this.errorService.handleApiError(error, 'Profile Completion');
    } finally {
      this.loading = false;
      this.errorService.setLoading('profile-completion', false);
    }
  }

  /**
   * Redeem a pending invite code after the user profile has been saved.
   * On success the user is added to the invited company and routed to the
   * dashboard through /logging-in (which re-validates the session).
   */
  private async redeemPendingInvite(inviteCode: string): Promise<void> {
    try {
      const result = await this.companyInviteService.redeemInviteViaApi(inviteCode);
      if (result.success && result.companyId) {
        // Update local user model with the new company information so the
        // post-auth flow can see the company without needing a Firestore reload.
        if (this.user) {
          const updatedCompanies = Array.from(
            new Set([...(this.user.companies || []), result.companyId]),
          );
          this.user.companies = updatedCompanies;
          this.user.currentCompanyId = result.companyId;
          if (!this.user.companyRoles) {
            this.user.companyRoles = {};
          }
          this.user.companyRoles[result.companyId] = result.role || 'user';
          this.userService.setUserModel(this.user);
        }
        // Republish the session from Firestore before routing. The comment
        // below used to say the post-auth flow could see the company "without
        // needing a Firestore reload" — but that flow reads AuthService, which
        // still holds the user hydrated before the invite was redeemed (#524).
        await this.authService.refreshHydratedUser();
        // Route through /logging-in so the full session/company state is refreshed
        await this.router.navigate(['/logging-in']);
      } else if (result.code === 'EMAIL_NOT_VERIFIED') {
        // The invite waits for a verified address (#874); creating a company
        // here instead would strand the user outside the team that invited them.
        await this.router.navigate(['/verify-email']);
      } else {
        // Redemption failed (e.g. wrong email, already member) – fall through to
        // the normal registration flow so the user can still create a company.
        this.loggingService.logWarning('Invite redemption failed: ' + result.error);
        this.proceedToNextStep();
      }
    } catch (error: any) {
      if (error?.error?.code === 'EMAIL_NOT_VERIFIED') {
        await this.router.navigate(['/verify-email']);
        return;
      }
      // Non-fatal: log and continue with the normal registration flow
      this.loggingService.logWarning('Error redeeming invite during profile completion: ' + (error?.message ?? error));
      this.proceedToNextStep();
    }
  }

  /**
   * Proceed to the next step in the registration flow based on tier and completion status
   */
  async proceedToNextStep(): Promise<void> {

    if (!this.user) {
      console.error('🚫 PROFILE COMPLETION - Cannot proceed: no user');
      return;
    }

    // Try to load company data before navigation
    let company: Company | null = null;

    try {
      // Determine which company ID to use (prefer currentCompanyId)
      const currentCompanyIdToLoad = this.user.currentCompanyId;

      if (currentCompanyIdToLoad) {
        const loadedCompany = await this.companyService.getDocument(currentCompanyIdToLoad);
        company = loadedCompany ? new Company(loadedCompany as any) : null;

        if (!company) {
          console.warn('⚠️ PROFILE COMPLETION - Company not found in Firestore:', { currentCompanyId: currentCompanyIdToLoad });

          // Check if user has other companies they belong to
          if (this.user.companies && this.user.companies.length > 0) {
            // Try to load the first company from the array
            for (const cid of this.user.companies) {
              const altLoadedCompany = await this.companyService.getDocument(cid);
              if (altLoadedCompany) {
                const altCompany = new Company(altLoadedCompany as any);
                company = altCompany;
                // Update user's currentCompanyId to this valid company
                this.user.currentCompanyId = cid;
                await this.userService.update(this.user, this.user.uid);
                break;
              }
            }
          }
        }
      } else {
        // Check if user belongs to any companies
        if (this.user.companies && this.user.companies.length > 0) {
          // Try to load the first valid company
          for (const cid of this.user.companies) {
            const altLoadedCompany = await this.companyService.getDocument(cid);
            if (altLoadedCompany) {
              const altCompany = new Company(altLoadedCompany as any);
              company = altCompany;
              // Set this as the user's current company
              this.user.currentCompanyId = cid;
              await this.userService.update(this.user, this.user.uid);
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ PROFILE COMPLETION - Error loading company:', error);
      company = null;
    }

    // Navigate with company data (or undefined if not available)
    this.profileValidationService.navigateToNextRegistrationStep(
      this.user,
      company || undefined,
      this.userTier,
    );
  }

  /**
   * Skip profile completion (only allowed for free tier or when all required fields are complete)
   */
  onSkip(): void {
    if (!this.user) return;

    // For trial/premium tiers, all fields are required - no skipping allowed
    if (this.isTrialOrPremium) {
      this.serverMessage =
        'All profile fields are required for your selected tier. Please complete the form.';
      return;
    }

    // Free tier: only allow skip if required fields (first/last name) are complete
    if (!this.needsFirstName && !this.needsLastName) {
      this.proceedToNextStep();
    } else {
      this.serverMessage = 'First and last name are required to continue.';
    }
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.profileForm.controls).forEach((field) => {
      const control = this.profileForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  // Form field accessors
  get firstName() {
    return this.profileForm.get('firstName');
  }

  get lastName() {
    return this.profileForm.get('lastName');
  }

  get phone() {
    return this.profileForm.get('phone');
  }

  get jobTitle() {
    return this.profileForm.get('jobTitle');
  }

  get preferredContactMethod() {
    return this.profileForm.get('preferredContactMethod');
  }

  // Validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.profileForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.profileForm.get(fieldName);
    if (field?.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        const fieldDisplayNames: { [key: string]: string } = {
          firstName: 'First name',
          lastName: 'Last name',
          phone: 'Phone number',
          jobTitle: 'Job title',
          preferredContactMethod: 'Preferred contact method',
        };
        const displayName = fieldDisplayNames[fieldName] || fieldName;
        return `${displayName} is required${this.isTrialOrPremium ? ' for your selected tier' : ''}`;
      }
      if (field.errors['minlength']) {
        const fieldDisplayNames: { [key: string]: string } = {
          firstName: 'First name',
          lastName: 'Last name',
          jobTitle: 'Job title',
        };
        const displayName = fieldDisplayNames[fieldName] || fieldName;
        return `${displayName} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  /**
   * Get help text for tier-specific requirements
   */
  getTierRequirementText(): string {
    if (this.isTrialOrPremium) {
      return `${this.userTier} tier requires complete profile information for account setup.`;
    }
    return 'Additional fields are optional.';
  }

  /**
   * Check if any tier-specific fields are required but incomplete
   */
  hasTierSpecificIncompleteFields(): boolean {
    return (
      this.isTrialOrPremium &&
      (this.needsPhone || this.needsJobTitle || this.needsContactMethod)
    );
  }

  /**
   * Get all form validation errors for debugging
   */
  private getFormErrors(): Record<string, any> {
    const errors: Record<string, any> = {};
    Object.keys(this.profileForm.controls).forEach((key) => {
      const control = this.profileForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }
}
